import { useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  faCircleCheck,
  faCircleExclamation,
  faDownload,
} from '@fortawesome/pro-solid-svg-icons'

import { Alert } from 'Components/_theme/Alert/Alert'
import { Heading } from 'Components/_theme/Heading/Heading'
import { PrimaryButton } from 'Components/_theme/PrimaryButton/PrimaryButton'
import { Icon } from 'Components/Icon/Icon'

import { useCopyToClipboard } from 'Portal/src/hooks/useCopyToClipboard/useCopyToClipboard'
import { usePortalReturnLabelDownload } from 'Portal/src/hooks/usePortalReturnLabelDownload/usePortalReturnLabelDownload'

import type { PortalReturnLabelModalSuccessProps } from './PortalReturnLabelModal.types'

export const PortalReturnLabelModalSuccess = (
  props: PortalReturnLabelModalSuccessProps,
) => {
  const { onClose, result } = props
  const intl = useIntl()
  const trackingNumberId = useId()
  const { copied, copy } = useCopyToClipboard(result.trackingReference)
  const {
    download,
    isLoading: isDownloading,
    error: downloadError,
  } = usePortalReturnLabelDownload(
    result.trackingReference,
    result.organisationId,
    result.pdfDownloadUrl,
  )

  return (
    <div className="text-center">
      <div className="text-primary d-flex justify-content-center mb-5">
        <Icon icon={faCircleCheck} size={48} />
      </div>

      <Heading as={4} className="mb-3" size={4}>
        <FormattedMessage id="PublicRequestReturnLabel_success_title" />
      </Heading>

      <p className="text-muted mb-5">
        <FormattedMessage id="PublicRequestReturnLabel_success_description" />
      </p>

      <div className="PortalReturnLabelModalSuccess-downloadSection p-5 mb-5 text-start">
        <PrimaryButton
          className="w-100 mb-4"
          disabled={isDownloading}
          isLoading={isDownloading}
          onClick={download}
          size="large"
        >
          <FormattedMessage
            id={
              isDownloading
                ? 'PortalReturnLabelModal_success_downloading'
                : 'PublicRequestReturnLabel_success_download'
            }
          />
        </PrimaryButton>

        {downloadError && (
          <Alert
            className="mt-4 mb-2"
            iconLeft={faCircleExclamation}
            variant="danger"
          >
            <FormattedMessage id="PortalReturnLabelModal_success_download_error" />
          </Alert>
        )}

        <label htmlFor={trackingNumberId}>
          <FormattedMessage id="PublicRequestReturnLabel_success_trackingLabel" />
        </label>
        <div className="d-flex align-items-center gap-3 mt-2">
          <input
            className="form-control font-monospace flex-grow-1"
            id={trackingNumberId}
            readOnly
            type="text"
            value={result.trackingReference}
          />
          <PrimaryButton
            aria-label={intl.formatMessage({
              id: copied
                ? 'PublicRequestReturnLabel_success_copied'
                : 'PublicRequestReturnLabel_success_copy',
            })}
            onClick={copy}
            size="small"
          >
            <FormattedMessage
              id={
                copied
                  ? 'PublicRequestReturnLabel_success_copied'
                  : 'PublicRequestReturnLabel_success_copy'
              }
            />
          </PrimaryButton>
        </div>
      </div>

      {result.isDangerousGoods && (
        <Alert className="mb-5 p-4 text-start" variant="warning">
          <Heading as={5} className="mb-3" size={5}>
            <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsHeading" />
          </Heading>
          <p className="mb-4">
            <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsBody" />
          </p>
          <div className="d-flex justify-content-center">
            {/* Bearer-authenticated download; URL must not embed the JWT. */}
            <a
              className="Button Button-medium Button-icon-left TertiaryButton text-decoration-none"
              download
              href={result.dangerousGoodsPdf ?? '#'}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon icon={faDownload} size={16} />
              <span>
                <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsDownload" />
              </span>
            </a>
          </div>
        </Alert>
      )}

      <PrimaryButton onClick={onClose}>
        <FormattedMessage id="Close" />
      </PrimaryButton>
    </div>
  )
}
